import React, { FC, useCallback } from "react";
import {
  ArtifactV1SessionPart,
  NoteCreatedV1ArtifactPayload,
  ArtifactV1SessionPartKind,
} from "../../session.types";
import { SessionPartConfig, SessionPartContext } from "../session-part.types";
import styled from "@emotion/styled";
import { NoteInfo } from "./session-artifact-note-renderer.utils";
import { DateTime } from "luxon";
import { MDSSessionArtifactNoteEmbed } from "@/mds/components/sessions/session-parts/artifacts/session-artifact-note-embed";
import {
  useIsFirstPartInSessionGroup,
  useIsLastItemInSessionHistory,
  useIsLastPartInSessionGroup,
  useMDSSession,
} from "../../session-context";
import { MDSSessionArtifact } from "@/mds/components/sessions/session-context";
import {
  MDSBubble,
  MDSBubbleAlignment,
  MDSBubbleSlotName,
  MDSBubbleVariant,
} from "@/mds/components/bubble";
import { MDSBubbleSessionSenderIcon } from "@/mds/components/sessions/session-sender-icon";
import { MemCommonMentionKind } from "@mem-labs/common-editor";

interface Props {
  part: ArtifactV1SessionPart;
  context: SessionPartContext;
  config: SessionPartConfig;
}

export const MDSSessionArtifactNoteCreatedV1Part: FC<Props> = ({ part, context, config }) => {
  const payload = part.artifact_payload as NoteCreatedV1ArtifactPayload;
  const { viewArtifact } = useMDSSession();

  const noteInfo: NoteInfo = {
    noteId: payload.note_id,
    createdAt: DateTime.fromISO(payload.note_created_at),
    updatedAt: DateTime.fromISO(payload.note_updated_at),
    encodedContent: payload.encoded_content,
    primaryLabel: payload.primary_label,
    secondaryLabel: "Created Note",
    markdownContent: payload.markdown_content,
  };

  const handleArtifactClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (config.linkHandler) {
        config.linkHandler(MemCommonMentionKind.Note, payload.note_id, null, event);
        return;
      }

      // Fallback to viewArtifact
      const artifactInfo: MDSSessionArtifact = {
        kind: ArtifactV1SessionPartKind.NoteCreatedV1,
        payload,
      };

      viewArtifact({ payload: artifactInfo });
    },
    [config, payload, viewArtifact]
  );

  const isFirstInGroup = useIsFirstPartInSessionGroup({ part });
  const isLastInGroup = useIsLastPartInSessionGroup({ part });
  const isLastInSessionHistory = useIsLastItemInSessionHistory({ itemId: context.itemId });

  return (
    <Bubble
      variant={MDSBubbleVariant.Transparent}
      align={MDSBubbleAlignment.Left}
      isFirstInGroup={isFirstInGroup}
      isLastInGroup={isLastInGroup}
    >
      <MDSBubbleSessionSenderIcon
        slot={MDSBubbleSlotName.Icon}
        isVisible={isFirstInGroup}
        isLoopEligible={isLastInSessionHistory}
      />

      <MDSSessionArtifactNoteEmbed
        context={context}
        config={config}
        noteInfo={noteInfo}
        onClick={handleArtifactClick}
      />
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)<{ isFirstInGroup?: boolean; isLastInGroup?: boolean }>`
  margin-top: ${({ theme, isFirstInGroup }) => (isFirstInGroup ? theme.spacing["1"] : 0)};
  margin-bottom: ${({ theme }) => theme.spacing["4"]};

  > div {
    color: unset;
  }
`;
